import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { TriangleAlert } from "lucide-react";
import { TASK_STATUS, formatDateOnly } from "@brainlog/shared";
import { errorMessage, get, post } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { statusLabel } from "./taskShared";

interface Candidate {
  key: string;
  title: string;
  quote: string;
  line: number;
  checked: boolean;
  mentions: { mention: string; matches: { id: string; displayName: string }[] }[];
  assigneeId: string | null;
  assigneeExplanation: string;
  dueDate: string | null;
  dueDateExplanation: string | null;
  dateSuggestions: { date: string; phrase: string; explanation: string }[];
  likelyDuplicateOf: { id: string; title: string; status: string } | null;
}

interface Row {
  include: boolean;
  title: string;
  assigneeId: string;
  dueDate: string;
  kind: "task" | "milestone" | "deliverable";
  status: string;
}

interface Result {
  key: string;
  taskId: string;
  duplicate: boolean;
}

const sel = "h-8 w-full rounded-md border bg-background px-2 text-sm";

/** Reviewed conversion of a note's action items into source-linked backlog tasks. Works without AI. */
export function ActionItemsDialog({ open, onOpenChange, noteId, workspaceId }: { open: boolean; onOpenChange(o: boolean): void; noteId: string; workspaceId: string }) {
  const qc = useQueryClient();
  const data = useQuery({
    queryKey: ["action-items", noteId],
    queryFn: () => get<{ candidates: Candidate[]; today: string; timezone: string; members: { id: string; displayName: string }[] }>(`/api/notes/${noteId}/action-items`),
    enabled: open,
    staleTime: 0,
  });
  const [rows, setRows] = useState<Record<string, Row>>({});
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<Result[] | null>(null);

  useEffect(() => {
    if (!data.data) return;
    setRows(
      Object.fromEntries(
        data.data.candidates.map((c) => [
          c.key,
          { include: !c.likelyDuplicateOf && !c.checked, title: c.title, assigneeId: c.assigneeId ?? "", dueDate: c.dueDate ?? "", kind: "task", status: "todo" } satisfies Row,
        ]),
      ),
    );
    setResults(null);
  }, [data.data]);

  const update = (key: string, patch: Partial<Row>) => setRows((r) => ({ ...r, [key]: { ...r[key], ...patch } }));
  const candidates = data.data?.candidates ?? [];
  const selected = candidates.filter((c) => rows[c.key]?.include && rows[c.key]?.title.trim());

  const submit = async () => {
    if (!selected.length) return;
    setBusy(true);
    try {
      const r = await post<{ results: Result[] }>("/api/tasks/from-note", {
        noteId,
        items: selected.map((c) => {
          const row = rows[c.key];
          return {
            key: c.key,
            title: row.title.trim(),
            description: "",
            quote: c.quote,
            assigneeId: row.assigneeId || null,
            dueDate: row.dueDate || null,
            kind: row.kind,
            status: row.status,
          };
        }),
      });
      setResults(r.results);
      const created = r.results.filter((x) => !x.duplicate).length;
      const existing = r.results.length - created;
      toast.success(`${created} task${created === 1 ? "" : "s"} created${existing ? `, ${existing} already existed` : ""}.`);
      await qc.invalidateQueries({ queryKey: ["tasks"] });
      await qc.invalidateQueries({ queryKey: ["overview"] });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Create tasks from action items</DialogTitle>
          <DialogDescription>
            Review each item before it becomes a backlog task. Tasks keep a link to this note and the exact line they came from. People are matched only with members of this notebook or
            project; dates are set only when written out. Nothing is created until you confirm.
          </DialogDescription>
        </DialogHeader>
        {data.isLoading && (
          <p className="text-sm text-muted-foreground" role="status">
            Reading the note…
          </p>
        )}
        {data.isError && (
          <p className="text-sm text-destructive" role="alert">
            {errorMessage(data.error)}
          </p>
        )}
        {data.data && !candidates.length && (
          <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">No action items found in this note.</p>
            <p className="mt-1">
              Write them as unchecked checkboxes (<code>- [ ] Send the protocol to Sam @Sam 2026-11-01</code>) anywhere in the note, or as list items under a heading such as
              “Actions” or “Next steps”.
            </p>
          </div>
        )}
        {candidates.length > 0 && (
          <ol className="space-y-3">
            {candidates.map((c) => {
              const row = rows[c.key];
              if (!row) return null;
              const res = results?.find((r) => r.key === c.key);
              return (
                <li key={c.key} className={cn("space-y-2 rounded-md border p-3", !row.include && "bg-muted/40")}>
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id={`ai-inc-${c.key}`}
                      checked={row.include}
                      onCheckedChange={(v) => update(c.key, { include: v === true })}
                      className="mt-1.5"
                      aria-label={`Include “${row.title}”`}
                    />
                    <div className="min-w-0 flex-1 space-y-1">
                      <label htmlFor={`ai-title-${c.key}`} className="sr-only">
                        Task title
                      </label>
                      <Input id={`ai-title-${c.key}`} value={row.title} onChange={(e) => update(c.key, { title: e.target.value })} maxLength={500} />
                      <p className="text-xs text-muted-foreground">
                        From line {c.line}: <span className="font-mono">{c.quote}</span>
                        {c.checked && " (already ticked in the note)"}
                      </p>
                    </div>
                  </div>
                  {c.likelyDuplicateOf && (
                    <p className="flex items-start gap-1.5 text-xs text-warning" role="note">
                      <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                      Likely duplicate of the existing task “{c.likelyDuplicateOf.title}” ({statusLabel(c.likelyDuplicateOf.status)}). Not selected by default.
                    </p>
                  )}
                  <div className="grid gap-2 sm:grid-cols-4">
                    <div>
                      <label className="text-xs text-muted-foreground" htmlFor={`ai-as-${c.key}`}>
                        Assignee
                      </label>
                      <select id={`ai-as-${c.key}`} className={sel} value={row.assigneeId} onChange={(e) => update(c.key, { assigneeId: e.target.value })}>
                        <option value="">Unassigned</option>
                        {data.data!.members.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.displayName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground" htmlFor={`ai-due-${c.key}`}>
                        Due date
                      </label>
                      <input id={`ai-due-${c.key}`} type="date" className={sel} value={row.dueDate} onChange={(e) => update(c.key, { dueDate: e.target.value })} />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground" htmlFor={`ai-kind-${c.key}`}>
                        Kind
                      </label>
                      <select id={`ai-kind-${c.key}`} className={sel} value={row.kind} onChange={(e) => update(c.key, { kind: e.target.value as Row["kind"] })}>
                        <option value="task">Task</option>
                        <option value="milestone">Milestone</option>
                        <option value="deliverable">Deliverable</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground" htmlFor={`ai-st-${c.key}`}>
                        Status
                      </label>
                      <select id={`ai-st-${c.key}`} className={sel} value={row.status} onChange={(e) => update(c.key, { status: e.target.value })}>
                        {TASK_STATUS.values.map((s) => (
                          <option key={s} value={s}>
                            {TASK_STATUS.labels[s]}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <p>Assignee: {c.assigneeExplanation}</p>
                    {c.dueDateExplanation && <p>Due date: {c.dueDateExplanation}</p>}
                    {!c.dueDate && !c.dateSuggestions.length && <p>Due date: none written — left empty.</p>}
                    {c.dateSuggestions.map((s) => (
                      <div key={s.phrase} className="flex flex-wrap items-center gap-2">
                        <span>Suggested interpretation (not applied): {s.explanation}</span>
                        <Button size="xs" variant="outline" onClick={() => update(c.key, { dueDate: s.date })} disabled={row.dueDate === s.date}>
                          Use suggested date {s.date}
                        </Button>
                      </div>
                    ))}
                    {row.dueDate && <p>Will be saved as {formatDateOnly(row.dueDate)} (calendar date, no time zone).</p>}
                  </div>
                  {res && (
                    <p className="text-xs font-medium" role="status">
                      {res.duplicate ? "Already existed — no duplicate created." : "Task created."}
                    </p>
                  )}
                </li>
              );
            })}
          </ol>
        )}
        <DialogFooter className="items-center gap-2 sm:justify-between">
          <span className="text-xs text-muted-foreground">{data.data ? `Dates interpreted relative to ${data.data.today} (${data.data.timezone}).` : ""}</span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              {results ? "Close" : "Cancel"}
            </Button>
            <Button onClick={submit} disabled={busy || !selected.length}>
              {busy ? "Creating…" : `Create ${selected.length} task${selected.length === 1 ? "" : "s"}`}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
